import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { addBusinessDays, addDays, isAfter, isBefore } from 'date-fns';
import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';
import { makeAPIRequestReactQuery } from '@/react-query/api';
import { endpoints } from '@/react-query/endpoints';

import type { TrialStatus, TrialStatusData } from './types';

export const useGetTrialStatus = () => {
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  return useQuery({
    queryKey: [
      endpoints.reverseTrial,
      primaryOrganizationId,
      primaryPracticeId,
      endpoints.status,
    ],
    queryFn: async () => {
      return await makeAPIRequestReactQuery<TrialStatusData>(
        `/${endpoints.reverseTrial}/${primaryOrganizationId}/${primaryPracticeId}/${endpoints.status}`,
      );
    },
    enabled: !!primaryPracticeId,
  });
};

interface UpdateTrialStatusVariables {
  status: TrialStatus;
  metadata?: Record<string, any>;
}

export const useUpdateTrialStatus = () => {
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  return useMutation<
    void,
    AxiosError<{ status: number; description: string }>,
    UpdateTrialStatusVariables,
    unknown
  >({
    mutationFn: async (variables) => {
      return await makeAPIRequestReactQuery(
        `/${endpoints.reverseTrial}/${primaryOrganizationId}/${primaryPracticeId}/${endpoints.status}`,
        {},
        'PATCH',
        {
          data: {
            ...variables,
          },
        },
      );
    },
  });
};

const getIsWithinThreeBusinessDays = (startDate: string) => {
  const start = new Date(startDate);
  const end = addBusinessDays(start, 3);
  return isBefore(new Date(), end);
};

export const useIsWithinThreeBusinessDaysOfStart = (
  trialStatusData?: TrialStatusData,
) => {
  const trialStartedOn = trialStatusData?.trialStartedOn;
  const trialEndedOn = trialStatusData?.trialEndedOn;
  const paidStartedOn = trialStatusData?.paidStartedOn;
  const paidEndedOn = trialStatusData?.paidEndedOn;

  const isOnboardingTrial =
    !!trialStartedOn &&
    !trialEndedOn &&
    getIsWithinThreeBusinessDays(trialStartedOn);
  const isOnboardingPaid =
    !!paidStartedOn &&
    !paidEndedOn &&
    !trialEndedOn && // Don't show alert if it was already shown during the trial
    getIsWithinThreeBusinessDays(paidStartedOn);

  return isOnboardingTrial || isOnboardingPaid;
};

const getIsAfterOneBusinessDay = (startDate: string) => {
  const start = new Date(startDate);
  const end = addBusinessDays(start, 1);
  return isAfter(new Date(), end);
};

export const useIsOneBusinessDayAfterStart = (
  trialStatusData?: TrialStatusData,
) => {
  const trialStartedOn = trialStatusData?.trialStartedOn;
  const trialEndedOn = trialStatusData?.trialEndedOn;
  const paidStartedOn = trialStatusData?.paidStartedOn;
  const paidEndedOn = trialStatusData?.paidEndedOn;

  const isOneBusinessDayAfterTrialStart =
    !!trialStartedOn &&
    !trialEndedOn &&
    getIsAfterOneBusinessDay(trialStartedOn);
  const isOneBusinessDayAfterPaidStart =
    !!paidStartedOn && !paidEndedOn && getIsAfterOneBusinessDay(paidStartedOn);

  return isOneBusinessDayAfterTrialStart || isOneBusinessDayAfterPaidStart;
};

export const useIsFirstWeekOfTrial = (trialStatusData?: TrialStatusData) => {
  const trialStartedOn = trialStatusData?.trialStartedOn;
  const trialEndedOn = trialStatusData?.trialEndedOn;

  return (
    !!trialStartedOn &&
    !trialEndedOn &&
    isBefore(new Date(), addDays(new Date(trialStartedOn), 7))
  );
};
